// src/components/Login.jsx
import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";

const API_BASE = process.env.REACT_APP_API_BASE || "";

const teal = "#40BEB4";
const gold = "#FFC850";
const navy = "#071520";
const red  = "#ff4d6d";

const Login = () => {
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError]       = useState("");
  const [success, setSuccess]   = useState(false);
  const [loading, setLoading]   = useState(false);
  const [showPwd, setShowPwd]   = useState(false);
  const [focus, setFocus]       = useState({ user: false, pass: false });
  const [shake, setShake]       = useState(false);
  const [attempts, setAttempts] = useState(0);
  const [btnHover, setBtnHover] = useState(false);

  const navigate = useNavigate();

  // Inject keyframes once
  useEffect(() => {
    const id = "admin-login-shake";
    if (!document.getElementById(id)) {
      const s = document.createElement("style");
      s.id = id;
      s.textContent = `
        @keyframes shakeCard {
          0%,100% { transform: translateX(0); }
          15%      { transform: translateX(-8px); }
          30%      { transform: translateX(8px); }
          45%      { transform: translateX(-6px); }
          60%      { transform: translateX(6px); }
          75%      { transform: translateX(-3px); }
          90%      { transform: translateX(3px); }
        }
        @keyframes errorSlideIn {
          from { opacity: 0; transform: translateY(-6px); }
          to   { opacity: 1; transform: translateY(0); }
        }
      `;
      document.head.appendChild(s);
    }
  }, []);

  const triggerShake = () => {
    setShake(true);
    setTimeout(() => setShake(false), 580);
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setError("");

    if (!username.trim() || !password) {
      triggerShake();
      setError("simple:Please enter both username and password.");
      return;
    }

    // ── Block non-admin logins client-side ──
    if (username.trim().toLowerCase() !== "admin") {
      setAttempts((a) => a + 1);
      triggerShake();
      setError("WRONG_ROLE");
      return;
    }

    setLoading(true);
    try {
      const res = await fetch(`${API_BASE}/api/auth/login`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ username: username.trim(), password }),
      });
      const data = await res.json().catch(() => ({}));
      if (res.ok) {
        setSuccess(true);
      } else {
        setAttempts((a) => a + 1);
        triggerShake();
        setError(data?.error || data?.message || "INVALID_CREDS");
      }
    } catch (err) {
      console.error("Login error:", err);
      triggerShake();
      setError("simple:Network error. Check your connection and try again.");
    } finally {
      setLoading(false);
    }
  };

  // Resolve error display
  const isSimpleError = typeof error === "string" && error.startsWith("simple:");
  const simpleText    = isSimpleError ? error.replace("simple:", "") : null;
  const isRichError   = !isSimpleError && !!error;

  const getRichError = () => {
    if (error === "WRONG_ROLE") return {
      icon: "🚫",
      title: "Admin access only",
      detail: "This dashboard is restricted to Admin credentials. Please use the correct username.",
    };
    return {
      icon: attempts >= 3 ? "⚠️" : "✕",
      title: attempts >= 3 ? "Multiple failed attempts" : "Invalid credentials",
      detail: "The username or password is incorrect. Please try again.",
    };
  };

  const hasError = !!error;

  return (
    <div style={{
      minHeight: "100vh",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      background: "linear-gradient(160deg, #071520 0%, #0a1e2e 35%, #071018 100%)",
      position: "relative",
      overflow: "hidden",
      padding: "24px",
    }}>
      {/* Grid overlay */}
      <div style={{
        position: "absolute", inset: 0, pointerEvents: "none",
        backgroundImage: `
          linear-gradient(rgba(64,190,180,0.03) 1px, transparent 1px),
          linear-gradient(90deg, rgba(64,190,180,0.03) 1px, transparent 1px)
        `,
        backgroundSize: "48px 48px",
      }} />

      {/* Glow */}
      <div style={{
        position: "absolute", inset: 0, pointerEvents: "none",
        background: `
          radial-gradient(ellipse at 25% 25%, rgba(64,190,180,0.06) 0%, transparent 55%),
          radial-gradient(ellipse at 75% 75%, rgba(255,200,80,0.05) 0%, transparent 55%)
        `,
      }} />

      {/* Card */}
      <div style={{
        position: "relative",
        zIndex: 10,
        width: "100%",
        maxWidth: "400px",
        background: "rgba(10, 25, 40, 0.92)",
        border: `1px solid ${hasError ? "rgba(255,77,109,0.35)" : "rgba(64,190,180,0.2)"}`,
        borderRadius: "20px",
        padding: "44px 36px 36px",
        boxShadow: hasError
          ? `0 0 0 1px rgba(255,255,255,0.03) inset, 0 24px 64px rgba(0,0,0,0.6), 0 0 40px rgba(255,77,109,0.08)`
          : `0 0 0 1px rgba(255,255,255,0.03) inset, 0 24px 64px rgba(0,0,0,0.6), 0 0 48px rgba(64,190,180,0.07)`,
        transition: "border-color 0.3s, box-shadow 0.3s",
        animation: shake ? "shakeCard 0.55s ease" : "none",
      }}>
        {/* Top accent */}
        <div style={{
          position: "absolute", top: 0, left: "40px", right: "40px", height: "1px",
          background: hasError
            ? `linear-gradient(90deg, transparent, ${red}80, ${red}50, transparent)`
            : `linear-gradient(90deg, transparent, ${teal}80, ${gold}50, transparent)`,
          transition: "background 0.3s",
        }} />

        {/* Corner brackets */}
        {[
          { top: "12px",    left: "12px",  borderTop:    `1.5px solid ${teal}`, borderLeft:  `1.5px solid ${teal}`, borderRadius: "4px 0 0 0" },
          { top: "12px",    right: "12px", borderTop:    `1.5px solid ${teal}`, borderRight: `1.5px solid ${teal}`, borderRadius: "0 4px 0 0" },
          { bottom: "12px", left: "12px",  borderBottom: `1.5px solid ${gold}`, borderLeft:  `1.5px solid ${gold}`, borderRadius: "0 0 0 4px" },
          { bottom: "12px", right: "12px", borderBottom: `1.5px solid ${gold}`, borderRight: `1.5px solid ${gold}`, borderRadius: "0 0 4px 0" },
        ].map((s, i) => (
          <div key={i} style={{ position: "absolute", width: "20px", height: "20px", opacity: 0.45, ...s }} />
        ))}

        {/* Logo circle */}
        <div style={{
          width: "68px", height: "68px", borderRadius: "50%",
          background: "rgba(64,190,180,0.1)", border: `1.5px solid rgba(64,190,180,0.3)`,
          margin: "0 auto 20px",
          display: "flex", alignItems: "center", justifyContent: "center",
          fontSize: "26px",
          boxShadow: "0 0 20px rgba(64,190,180,0.1)",
        }}>
          🔐
        </div>

        <h2 style={{
          fontSize: "22px", fontWeight: 600, color: "#fff",
          textAlign: "center", marginBottom: "4px", letterSpacing: "-0.3px",
        }}>
          Dashboard <span style={{ color: teal }}>Access</span>
        </h2>
        <p style={{
          fontSize: "13px", color: "rgba(255,255,255,0.38)",
          textAlign: "center", marginBottom: "30px",
        }}>
          Admin credentials required
        </p>

        <form onSubmit={handleLogin}>
          {/* Username */}
          <div style={{ marginBottom: "16px" }}>
            <label style={{ display: "block", fontSize: "11px", fontWeight: 500, color: "rgba(255,255,255,0.45)", letterSpacing: "1.5px", textTransform: "uppercase", marginBottom: "7px" }}>
              Username
            </label>
            <input
              type="text"
              placeholder="Admin"
              value={username}
              onChange={(e) => { setUsername(e.target.value); setError(""); }}
              onFocus={() => setFocus({ ...focus, user: true })}
              onBlur={() => setFocus({ ...focus, user: false })}
              required
              style={{
                width: "100%", boxSizing: "border-box",
                padding: "12px 16px",
                background: "rgba(255,255,255,0.04)",
                border: hasError
                  ? "1px solid rgba(255,77,109,0.45)"
                  : focus.user
                  ? `1px solid rgba(64,190,180,0.5)`
                  : "1px solid rgba(255,255,255,0.1)",
                boxShadow: hasError
                  ? "0 0 0 3px rgba(255,77,109,0.08)"
                  : focus.user
                  ? "0 0 0 3px rgba(64,190,180,0.08)"
                  : "none",
                borderRadius: "10px", color: "#fff", fontSize: "14px", outline: "none",
                transition: "border-color 0.2s, box-shadow 0.2s",
              }}
            />
          </div>

          {/* Password */}
          <div style={{ marginBottom: "20px" }}>
            <label style={{ display: "block", fontSize: "11px", fontWeight: 500, color: "rgba(255,255,255,0.45)", letterSpacing: "1.5px", textTransform: "uppercase", marginBottom: "7px" }}>
              Password
            </label>
            <div style={{ position: "relative" }}>
              <input
                type={showPwd ? "text" : "password"}
                placeholder="••••••••"
                value={password}
                onChange={(e) => { setPassword(e.target.value); setError(""); }}
                onFocus={() => setFocus({ ...focus, pass: true })}
                onBlur={() => setFocus({ ...focus, pass: false })}
                required
                style={{
                  width: "100%", boxSizing: "border-box",
                  padding: "12px 52px 12px 16px",
                  background: "rgba(255,255,255,0.04)",
                  border: hasError
                    ? "1px solid rgba(255,77,109,0.45)"
                    : focus.pass
                    ? `1px solid rgba(64,190,180,0.5)`
                    : "1px solid rgba(255,255,255,0.1)",
                  boxShadow: hasError
                    ? "0 0 0 3px rgba(255,77,109,0.08)"
                    : focus.pass
                    ? "0 0 0 3px rgba(64,190,180,0.08)"
                    : "none",
                  borderRadius: "10px", color: "#fff", fontSize: "14px", outline: "none",
                  transition: "border-color 0.2s, box-shadow 0.2s",
                }}
              />
              <button
                type="button"
                onClick={() => setShowPwd(!showPwd)}
                style={{
                  position: "absolute", right: "14px", top: "50%", transform: "translateY(-50%)",
                  background: "none", border: "none", cursor: "pointer",
                  fontSize: "11px", fontWeight: 500, letterSpacing: "1px",
                  textTransform: "uppercase", color: "rgba(255,200,80,0.7)",
                }}
              >
                {showPwd ? "Hide" : "Show"}
              </button>
            </div>
          </div>

          {/* ── SIMPLE ERROR ── */}
          {isSimpleError && (
            <div style={{
              display: "flex", alignItems: "center", gap: "8px",
              background: "rgba(255,77,109,0.1)", border: "1px solid rgba(255,77,109,0.25)",
              borderRadius: "8px", padding: "10px 13px",
              color: "#ff7a91", fontSize: "13px", marginBottom: "16px",
              animation: "errorSlideIn 0.25s ease",
            }}>
              <span style={{ fontSize: "14px" }}>⚠</span>
              <span>{simpleText}</span>
            </div>
          )}

          {/* ── RICH ERROR ── */}
          {isRichError && (() => {
            const rich = getRichError();
            return (
              <div style={{
                background: "linear-gradient(135deg, rgba(255,77,109,0.08), rgba(255,77,109,0.04))",
                border: "1px solid rgba(255,77,109,0.28)",
                borderRadius: "12px", padding: "14px 16px",
                marginBottom: "16px",
                animation: "errorSlideIn 0.3s ease",
                position: "relative", overflow: "hidden",
              }}>
                {/* Left stripe */}
                <div style={{
                  position: "absolute", left: 0, top: 0, bottom: 0, width: "3px",
                  background: `linear-gradient(180deg, ${red}, #ff8fa3)`,
                  borderRadius: "12px 0 0 12px",
                }} />
                <div style={{ display: "flex", alignItems: "center", gap: "8px", marginBottom: "5px" }}>
                  <span style={{ fontSize: "16px" }}>{rich.icon}</span>
                  <span style={{ fontSize: "13px", fontWeight: 600, color: "#ff7a91" }}>{rich.title}</span>
                </div>
                <div style={{ fontSize: "12px", color: "rgba(255,180,190,0.75)", paddingLeft: "24px", lineHeight: 1.5 }}>
                  {rich.detail}
                </div>
                <div style={{
                  marginTop: "8px", paddingLeft: "24px",
                  display: "flex", alignItems: "center", gap: "6px",
                  fontSize: "11px", color: "rgba(64,190,180,0.7)", letterSpacing: "0.3px",
                }}>
                  <span style={{ fontSize: "10px" }}>●</span>
                  Enter new credentials and try again
                </div>
              </div>
            );
          })()}

          {/* Sign In */}
          <button
            type="submit"
            disabled={loading}
            onMouseEnter={() => setBtnHover(true)}
            onMouseLeave={() => setBtnHover(false)}
            style={{
              width: "100%", padding: "14px",
              background: loading
                ? "rgba(64,190,180,0.3)"
                : `linear-gradient(135deg, #1a8a82 0%, ${teal} 50%, #1a9e94 100%)`,
              border: "none", borderRadius: "10px",
              color: navy, fontSize: "14px", fontWeight: 700,
              letterSpacing: "1.5px", textTransform: "uppercase",
              cursor: loading ? "not-allowed" : "pointer",
              boxShadow: loading ? "none" : btnHover ? "0 6px 28px rgba(64,190,180,0.35)" : "0 4px 20px rgba(64,190,180,0.25)",
              transform: btnHover && !loading ? "translateY(-1px)" : "none",
              transition: "transform 0.15s, box-shadow 0.15s",
              marginBottom: "12px",
            }}
          >
            {loading ? "Signing in…" : "Sign In"}
          </button>

          {/* Clear */}
          <button
            type="button"
            onClick={() => { setUsername(""); setPassword(""); setError(""); }}
            style={{
              width: "100%", padding: "13px",
              background: "rgba(255,255,255,0.04)",
              border: "1px solid rgba(255,255,255,0.12)",
              borderRadius: "10px", color: "rgba(255,255,255,0.55)",
              fontSize: "14px", fontWeight: 500, cursor: "pointer",
              transition: "background 0.15s",
            }}
          >
            Clear
          </button>
        </form>

        {/* Decorative dots */}
        <div style={{ display: "flex", justifyContent: "center", gap: "6px", marginTop: "24px" }}>
          <div style={{ width: "6px", height: "6px", borderRadius: "50%", background: teal, boxShadow: `0 0 6px ${teal}` }} />
          <div style={{ width: "6px", height: "6px", borderRadius: "50%", background: "rgba(64,190,180,0.4)" }} />
          <div style={{ width: "6px", height: "6px", borderRadius: "50%", background: "rgba(255,200,80,0.4)" }} />
          <div style={{ width: "6px", height: "6px", borderRadius: "50%", background: gold, boxShadow: `0 0 6px ${gold}` }} />
        </div>
      </div>

      {/* Success modal */}
      {success && (
        <div style={{
          position: "fixed", inset: 0, zIndex: 50,
          display: "flex", alignItems: "center", justifyContent: "center",
          background: "rgba(0,0,0,0.75)", backdropFilter: "blur(6px)",
        }}>
          <div style={{
            background: "linear-gradient(160deg, #0d1f2d 0%, #071520 100%)",
            border: `1px solid rgba(64,190,180,0.3)`,
            borderRadius: "18px", padding: "36px 32px",
            textAlign: "center", maxWidth: "320px", width: "90%",
            boxShadow: "0 0 60px rgba(64,190,180,0.15)",
          }}>
            <div style={{ fontSize: "36px", marginBottom: "14px" }}>✅</div>
            <h2 style={{ fontSize: "19px", fontWeight: 600, color: teal, marginBottom: "8px" }}>
              Successfully logged in!
            </h2>
            <p style={{ color: "rgba(255,255,255,0.45)", fontSize: "13px", marginBottom: "26px" }}>
              Welcome, Admin. Click OK to open the dashboard.
            </p>
            <button
              onClick={() => navigate("/dashboard")}
              style={{
                padding: "12px 44px", fontSize: "14px", fontWeight: 700,
                color: navy, background: `linear-gradient(135deg, ${teal} 0%, #1a9e94 100%)`,
                border: "none", borderRadius: "8px", cursor: "pointer",
                textTransform: "uppercase", letterSpacing: "1px",
                boxShadow: "0 4px 16px rgba(64,190,180,0.3)",
              }}
            >
              OK
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default Login;
